import React, { useState } from 'react';
import { useServers, useClients } from '../hooks';
import { ServerCard, ServerForm, ServerDeletionModal } from '../components/servers';
import { Button, Modal, Spinner, Alert } from '../components/ui';
import { Plus, RefreshCw } from 'lucide-react';
import { api } from '../services/api';

export const Servers: React.FC = () => {
  const { servers, loading, error, connectServer, disconnectServer, fetchServers } = useServers();
  const { clients } = useClients();
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [editingServer, setEditingServer] = useState<any>(null);
  const [serverToDelete, setServerToDelete] = useState<{id: string, name: string} | null>(null);
  const [editName, setEditName] = useState('');
  const [editClientId, setEditClientId] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [editError, setEditError] = useState('');

  const handleConnect = async (serverName: string, url: string, clientId?: string) => {
    await connectServer(serverName, url, clientId);
    setShowConnectModal(false);
  };

  const handleDeleteClick = (id: string) => {
    const server = servers.find(s => s.id === id);
    if (server) {
      setServerToDelete({ id: server.id, name: server.serverName });
      setShowDeletionModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!serverToDelete) return;

    try {
      await disconnectServer(serverToDelete.id);
      setShowDeletionModal(false);
      setServerToDelete(null);
    } catch (err) {
      console.error('Failed to disconnect server:', err);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchServers();
    } catch (err) {
      console.error('Failed to refresh servers:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleEdit = (serverId: string) => {
    const server = servers.find(s => s.id === serverId);
    if (server) {
      setEditingServer(server);
      setEditName(server.serverName);
      setEditClientId(server.clientId);
      setShowEditModal(true);
      setEditError('');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingServer) return;

    try {
      setEditError('');
      await api.servers.update(editingServer.id, {
        server_name: editName,
        client_id: editClientId
      });

      setShowEditModal(false);
      setEditingServer(null);
      await fetchServers();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update server');
    }
  };

  if (loading && servers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Servers</h1>
          <p className="text-gray-600 mt-1">
            Connect and manage your MCP servers
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowConnectModal(true)}
          >
            <Plus size={18} className="mr-2" />
            Connect Server
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {/* Servers Grid */}
      {servers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">No servers connected yet</p>
          <Button
            variant="primary"
            onClick={() => setShowConnectModal(true)}
          >
            <Plus size={18} className="mr-2" />
            Connect Your First Server
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servers.map((server) => (
            <ServerCard
              key={server.id}
              {...server}
              onDelete={handleDeleteClick}
              onRefresh={handleRefresh}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {/* Connect Modal */}
      <Modal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        title="Connect MCP Server"
        size="md"
      >
        <ServerForm
          onSubmit={handleConnect}
          onCancel={() => setShowConnectModal(false)}
          clients={clients}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Server"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Server Name
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client
            </label>
            <select
              value={editClientId}
              onChange={(e) => setEditClientId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {clients.map((client: any) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Moving this server will also move all its tools, prompts, and resources
            </p>
          </div>

          {editError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
              {editError}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              variant="primary"
              onClick={handleSaveEdit}
              className="flex-1"
            >
              Save Changes
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowEditModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Server Deletion Modal */}
      {serverToDelete && (
        <ServerDeletionModal
          isOpen={showDeletionModal}
          onClose={() => {
            setShowDeletionModal(false);
            setServerToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          serverId={serverToDelete.id}
          serverName={serverToDelete.name}
        />
      )}
    </div>
  );
};
